import { formatCurrency } from '~libs/dataUtils';
import { formatMoney, formatOverAmount, formatRate, formatWholeMoney } from '~support-at-home/lib/budgetFormat';
import {
  amountBeyond,
  mostSeriousTone,
  OverLimit,
  OverTone,
  splitOverLimit,
  toCents,
  WithinLimit,
  withinLimit,
} from '~support-at-home/lib/overLimits';

import type { CalloutTone } from '~components/Callout';
import type { TreeTableRow } from '~components/tree-table/types';
import type { DonutSegment } from '~components/visualisation/donut/Donut';
import type { FigureTileProps } from '~components/visualisation/figure-tile/FigureTile';
import type { FigureTileItem } from '~components/visualisation/figure-tile/FigureTileGroup';
import type { BarSegment } from '~components/visualisation/segmented-bar/SegmentedBar';
import type { SegmentedBarLegendItem } from '~components/visualisation/segmented-bar/SegmentedBarLegend';
import { wholeMoney } from '~support-at-home/components/common/WholeMoney';

import {
  Tracker,
  TrackerCaseManagement,
  TrackerCategory,
  TrackerFigures,
  TrackerService,
  TrackerSummary,
} from '~support-at-home/models';

import type { TrackerLegendItem } from './TrackerLegend';
import { TRACKER_FIGURE_COLOURS } from './trackerPalette';
import { TRACKER_TEXT } from './trackerText';

const UNCATEGORISED_NAME = 'Uncategorised';
// The one group every service with a missing or unknown category goes into
const UNCATEGORISED_ID = 'uncategorised';

export interface TrackerBanner {
  tone: CalloutTone;
  title: string;
  lines: string[];
}

const whoIsOver = ({ booked, claimed }: OverLimit): string => {
  const copy = TRACKER_TEXT.banner;
  if (booked > 0 && claimed > 0) return copy.bookedAndClaimed;
  return claimed > 0 ? copy.claimed : copy.booked;
};

/** A category holding the whole budget: its line would only repeat the agreed budget line (Elsa, Ada). */
const isWholeBudget = (category: TrackerCategory, summary: TrackerSummary): boolean =>
  (['allocated', 'booked', 'claimed'] as const).every((key) => toCents(category[key]) === toCents(summary[key]));

export interface OverItem {
  key: string;
  tone: OverTone;
  text: string;
}

/**
 * Every limit the budget is over, in this order: funding spent, agreed budget over funding, agreed budget, care
 * management, then categories in the API's order.
 */
export const listOverLimits = (tracker: Tracker): OverItem[] => {
  const { summary, available, categories = [], case_management: careManagement } = tracker;
  const copy = TRACKER_TEXT.banner;
  const items: OverItem[] = [];

  const overFunding = splitOverLimit(available, summary);
  if (overFunding.total > 0) {
    items.push({
      key: 'funding-used',
      tone: 'danger',
      text: copy.overFunding(whoIsOver(overFunding), formatOverAmount(overFunding.total), formatWholeMoney(available)),
    });
  }

  const budgetOverFunding = amountBeyond(summary.allocated, available);
  if (budgetOverFunding > 0) {
    items.push({
      key: 'funding-allocated',
      tone: 'danger',
      text: copy.agreedBudgetOverFunding(formatOverAmount(budgetOverFunding), formatWholeMoney(available)),
    });
  }

  const overBudget = splitOverLimit(summary.allocated, summary);
  if (overBudget.total > 0) {
    items.push({
      key: 'agreed-budget',
      // Over the agreed budget, not the funding: amber (TAV-3482)
      tone: 'warning',
      text: copy.overAgreedBudget(
        whoIsOver(overBudget),
        formatOverAmount(overBudget.total),
        formatWholeMoney(summary.allocated),
      ),
    });
  }

  if (careManagement) {
    const over = splitOverLimit(careManagement.allowance, careManagement);
    if (over.total > 0) {
      items.push({
        key: 'care-management',
        tone: 'warning',
        text: copy.overAllowance(
          whoIsOver(over),
          formatOverAmount(over.total),
          formatWholeMoney(careManagement.allowance),
        ),
      });
    }
  }

  categories.forEach((category) => {
    if (isWholeBudget(category, summary)) return;
    const over = splitOverLimit(category.allocated, category);
    if (over.total > 0) {
      items.push({
        key: `category-${category.id}`,
        tone: 'warning',
        text: copy.overCategory(
          whoIsOver(over),
          category.name,
          formatOverAmount(over.total),
          formatWholeMoney(category.allocated),
        ),
      });
    }
  });

  return items;
};

/**
 * One banner for everything over a limit, in the colour of the most serious item; null when nothing is. One item is
 * its own heading; several get a heading and one line each.
 */
export const buildTrackerBanner = (tracker: Tracker): TrackerBanner | null => {
  const items = listOverLimits(tracker);
  const tone = mostSeriousTone(items.map((item) => item.tone));
  if (!tone) return null;
  return { tone, title: TRACKER_TEXT.banner.title(items.length), lines: items.map(({ text }) => text) };
};

/** The pill on the figure that is over. Over funding outranks Over budget. */
const tileFlag = (
  overFunding: number,
  overBudget: number,
  budgetTone: OverTone,
): Partial<Pick<FigureTileProps, 'tone' | 'pill'>> => {
  if (overFunding > 0) return { tone: 'danger', pill: TRACKER_TEXT.pills.overFunding };
  if (overBudget > 0) return { tone: budgetTone, pill: TRACKER_TEXT.pills.overBudget };
  return {};
};

const careManagementSentence = (tracker: Tracker): string | null => {
  const { total_includes_case_management: included } = tracker;
  if (included === null || included === undefined) return null;
  return included ? TRACKER_TEXT.allocation.careManagementIncluded : TRACKER_TEXT.allocation.careManagementAdded;
};

/** Available = Allocated + Residual, then Allocation rate after a divider. */
export const transformAllocationTiles = (tracker: Tracker): FigureTileItem[] => {
  const { summary, available, allocation_percentage } = tracker;
  const copy = TRACKER_TEXT.allocation;
  const isOverAllocated = amountBeyond(summary.allocated, available) > 0;
  const isRateOver = allocation_percentage !== null && allocation_percentage > 1;
  const availableCaption = [TRACKER_TEXT.summary.available, careManagementSentence(tracker)].filter(Boolean).join(' ');

  return [
    {
      key: 'available',
      label: 'Available',
      value: wholeMoney(available),
      description: copy.availableLine,
      caption: { id: 'allocation-available', content: availableCaption },
    },
    {
      key: 'allocated',
      label: 'Allocated',
      value: wholeMoney(summary.allocated),
      description: copy.allocatedLine,
      caption: { id: 'allocation-allocated', content: TRACKER_TEXT.summary.allocated },
      joinBefore: 'equals',
    },
    {
      key: 'residual',
      label: 'Residual',
      value: wholeMoney(summary.unallocated),
      description: copy.residualLine,
      ...(isOverAllocated && { tone: 'danger' as const, pill: TRACKER_TEXT.pills.overFunding }),
      caption: { id: 'allocation-residual', content: copy.residual },
      joinBefore: 'plus',
    },
    // An empty cell, so the rate lines up with the Utilisation rate below (five cells each, TAV-3482)
    { key: 'allocation-blank', blank: true, joinBefore: 'space' },
    {
      key: 'allocation-rate',
      label: 'Allocation Rate',
      value: formatRate(allocation_percentage),
      description: copy.rateLine,
      ...(isRateOver && { tone: 'danger' as const }),
      caption: { id: 'allocation-rate', content: TRACKER_TEXT.summary.allocationRate },
      joinBefore: 'divider',
    },
  ];
};

/** Allocated = Booked + Claimed + Remaining, then Utilisation rate after a divider. */
export const transformUtilisationTiles = (tracker: Tracker): FigureTileItem[] => {
  const { summary, available } = tracker;
  const copy = TRACKER_TEXT.utilisation;
  const overFunding = splitOverLimit(available, summary);
  const overBudget = splitOverLimit(summary.allocated, summary);
  // Over funding is red, over the agreed budget amber; claimed counts first, so a tile is flagged when its own part
  // of the work is the part beyond the limit.
  const flag = (part: 'booked' | 'claimed') => tileFlag(overFunding[part], overBudget[part], 'warning');

  return [
    {
      key: 'allocated',
      label: 'Allocated',
      value: wholeMoney(summary.allocated),
      description: copy.allocatedLine,
      caption: { id: 'utilisation-allocated', content: TRACKER_TEXT.summary.allocated },
    },
    {
      key: 'booked',
      label: 'Booked',
      value: wholeMoney(summary.booked),
      description: copy.bookedLine,
      ...flag('booked'),
      caption: { id: 'utilisation-booked', content: TRACKER_TEXT.overview.booked },
      joinBefore: 'equals',
    },
    {
      key: 'claimed',
      label: 'Claimed',
      value: wholeMoney(summary.claimed),
      description: copy.claimedLine,
      ...flag('claimed'),
      caption: { id: 'utilisation-claimed', content: TRACKER_TEXT.overview.claimed },
      joinBefore: 'plus',
    },
    {
      key: 'remaining',
      label: 'Remaining',
      value: wholeMoney(summary.remaining),
      description: copy.remainingLine,
      caption: { id: 'utilisation-remaining', content: TRACKER_TEXT.overview.remaining },
      joinBefore: 'plus',
    },
    {
      key: 'utilisation-rate',
      label: 'Utilisation Rate',
      value: formatRate(summary.utilization_percentage),
      description: copy.rateLine,
      ...(overBudget.total > 0 && { tone: 'warning' as const }),
      caption: { id: 'utilisation-rate', content: copy.rate },
      joinBefore: 'divider',
    },
  ];
};

/** Distinct service ids: a service in two categories counts once. */
export const countDistinctServices = (services: TrackerService[]): number =>
  new Set(services.map((service) => service.id)).size;

export const pluralise = (count: number, singular: string, plural = `${singular}s`): string =>
  `${count} ${count === 1 ? singular : plural}`;

export interface TrackerSummaryView {
  banner: TrackerBanner | null;
  allocation: FigureTileItem[];
  utilisation: FigureTileItem[];
  serviceCount: number;
}

export const transformTrackerSummary = (tracker: Tracker): TrackerSummaryView => ({
  banner: buildTrackerBanner(tracker),
  allocation: transformAllocationTiles(tracker),
  utilisation: transformUtilisationTiles(tracker),
  serviceCount: countDistinctServices(tracker.services),
});

/** The second lap: its segment key, its tooltip label and its colour. */
export interface OverLap {
  key: string;
  label: string;
  color: string;
}

/** The second lap: one segment for the total beyond the limit; none when the total is within it. */
export const toOverLapSegments = (total: number, lap: OverLap): DonutSegment[] =>
  total > 0 ? [{ key: lap.key, label: lap.label, value: total, color: lap.color }] : [];

/**
 * The legend row for the part beyond a limit, with its booked and claimed parts as sub lines; none when within it.
 */
export const overLegendRows = (
  over: OverLimit,
  lap: OverLap & { caption: TrackerLegendItem['caption'] },
  labels: { booked: string; claimed: string },
): TrackerLegendItem[] =>
  over.total > 0
    ? [
        {
          key: lap.key,
          label: lap.label,
          value: over.total,
          over: true,
          color: lap.color,
          caption: lap.caption,
          subItems: [
            { key: 'booked', label: labels.booked, value: over.booked },
            { key: 'claimed', label: labels.claimed, value: over.claimed },
          ],
        },
      ]
    : [];

/** A ring that stops at its limit: Booked, Claimed, then Not yet booked, the parts within the limit only. */
export const toWithinLimitSegments = (within: WithinLimit): DonutSegment[] =>
  [
    {
      key: 'booked',
      label: TRACKER_TEXT.breakdown.legend.booked,
      value: within.booked,
      color: TRACKER_FIGURE_COLOURS.booked,
    },
    {
      key: 'claimed',
      label: TRACKER_TEXT.breakdown.legend.claimed,
      value: within.claimed,
      color: TRACKER_FIGURE_COLOURS.claimed,
    },
    {
      key: 'not-yet-booked',
      label: TRACKER_TEXT.overview.notYetBookedLabel,
      value: within.unused,
      color: TRACKER_FIGURE_COLOURS.remaining,
    },
  ].filter(({ value }) => value > 0);

/** The care management ring, full at the allowance: Used within it, then Unused. */
export const toCareManagementWithinSegments = (within: WithinLimit): DonutSegment[] =>
  [
    {
      key: 'used',
      label: TRACKER_TEXT.careManagement.legend.used,
      value: within.booked + within.claimed,
      color: TRACKER_FIGURE_COLOURS.careManagementUsed,
    },
    {
      key: 'unused',
      label: TRACKER_TEXT.careManagement.legend.unused,
      value: within.unused,
      color: TRACKER_FIGURE_COLOURS.careManagementRemaining,
    },
  ].filter(({ value }) => value > 0);

export interface TrackerOverviewView {
  /** The Allocated figure, e.g. "$2,600". */
  centreValue: string;
  /** Hover percentages are shares of Allocated. */
  percentBase: number;
  ariaLabel: string;
  segments: DonutSegment[];
  legend: TrackerLegendItem[];
  /** The second lap, when Booked and Claimed together come to more than Allocated: the total less Allocated. */
  overSegments: DonutSegment[];
  /** Booked and Claimed come to more than Allocated: the card gets the amber outline. */
  over: boolean;
}

export const transformTrackerOverview = (tracker: Tracker): TrackerOverviewView => {
  const { summary } = tracker;
  const copy = TRACKER_TEXT.overview;
  const within = withinLimit(summary.allocated, summary);
  const over = splitOverLimit(summary.allocated, summary);
  const lap: OverLap = {
    key: 'over-agreed-budget',
    label: copy.overLabel,
    color: TRACKER_FIGURE_COLOURS.bookedOverAllocated,
  };

  return {
    centreValue: formatCurrency(summary.allocated, 0),
    percentBase: summary.allocated,
    ariaLabel:
      `Allocated ${formatMoney(summary.allocated)}: booked ${formatMoney(summary.booked)}, ` +
      `claimed ${formatMoney(summary.claimed)}, not yet booked ${formatMoney(within.unused)}${
        over.total > 0 ? `, ${copy.overAria(formatMoney(over.total))}` : ''
      }`,
    // The ring stops at Allocated: only the parts within it are drawn; the part beyond it is the second lap
    segments: toWithinLimitSegments(within),
    overSegments: toOverLapSegments(over.total, lap),
    legend: [
      {
        key: 'booked',
        label: 'Booked',
        value: within.booked,
        color: TRACKER_FIGURE_COLOURS.booked,
        caption: { id: 'overview-booked', content: `${copy.booked} ${copy.withinAllocated}` },
      },
      {
        key: 'claimed',
        label: 'Claimed',
        value: within.claimed,
        color: TRACKER_FIGURE_COLOURS.claimed,
        caption: { id: 'overview-claimed', content: `${copy.claimed} ${copy.withinAllocated}` },
      },
      {
        key: 'not-yet-booked',
        label: copy.notYetBookedLabel,
        value: within.unused,
        color: TRACKER_FIGURE_COLOURS.remaining,
        caption: { id: 'overview-not-yet-booked', content: copy.notYetBooked },
      },
      // Booked, Claimed and Not yet booked above add up to Allocated (claimed first, as the ring); this row is the
      // rest, split into its booked and claimed parts, so nothing is counted twice (TAV-3482)
      ...overLegendRows(
        over,
        { ...lap, caption: { id: 'overview-over', content: copy.over } },
        { booked: copy.bookedOverLabel, claimed: copy.claimedOverLabel },
      ),
    ],
    over: over.total > 0,
  };
};

export type UtilisationFigures = Pick<TrackerFigures, 'allocated' | 'booked' | 'claimed' | 'remaining'>;

/**
 * The Budget Breakdown bars: Booked, Claimed and Remaining as parts of
 * Allocated, plus the part beyond Allocated in amber when Booked and Claimed come to more than it. Claimed counts
 * against Allocated first, whatever the draw order. Zero-value segments are dropped. The total width is
 * max(Allocated, Claimed + Booked).
 */
export const toUtilisationSegments = ({ allocated, booked, claimed }: UtilisationFigures): BarSegment[] => {
  const allocatedFloor = Math.max(0, allocated);
  const claimedFloor = Math.max(0, claimed);
  const bookedFloor = Math.max(0, booked);

  const claimedIn = Math.min(claimedFloor, allocatedFloor);
  const bookedIn = Math.min(bookedFloor, Math.max(0, allocatedFloor - claimedFloor));
  const remainingIn = Math.max(0, allocatedFloor - claimedFloor - bookedFloor);
  const over = Math.max(0, claimedFloor + bookedFloor - allocatedFloor);
  const { legend } = TRACKER_TEXT.breakdown;

  const segments: BarSegment[] = [
    { key: 'booked', label: legend.booked, value: bookedIn, color: TRACKER_FIGURE_COLOURS.booked },
    { key: 'claimed', label: legend.claimed, value: claimedIn, color: TRACKER_FIGURE_COLOURS.claimed },
    { key: 'remaining', label: legend.remaining, value: remainingIn, color: TRACKER_FIGURE_COLOURS.remaining },
    {
      key: 'booked-over-allocated',
      label: legend.bookedOverAllocated,
      value: over,
      color: TRACKER_FIGURE_COLOURS.bookedOverAllocated,
    },
  ];
  return segments.filter(({ value }) => value > 0);
};

export const describeUtilisation = ({ booked, claimed, remaining }: UtilisationFigures): string =>
  `Booked ${formatMoney(booked)}, claimed ${formatMoney(claimed)}, remaining ${formatMoney(remaining)}`;

export type CareManagementFigures = Pick<TrackerCaseManagement, 'allowance' | 'used' | 'remaining'>;

/** The part of care management Used beyond the allowance, zero when within it: the ring's orange part. */
const careManagementOverAllowance = ({ allowance, used }: CareManagementFigures): number =>
  Math.max(0, used - allowance);

/** The care management ring's aria-label: the allowance, Used and Remaining, and any Used beyond the allowance. */
export const describeCareManagement = (figures: CareManagementFigures): string => {
  const { allowance, used, remaining } = figures;
  const overAllowance = careManagementOverAllowance(figures);
  const overLabel = overAllowance > 0 ? `, over allowance ${formatMoney(overAllowance)}` : '';
  return `Allowance ${formatMoney(allowance)}: used ${formatMoney(used)}, remaining ${formatMoney(remaining)}${overLabel}`;
};

export interface BreakdownRow {
  kind: 'total' | 'category' | 'service';
  name: string;
  /** Service code, services only. */
  code: string | null;
  /** "3 categories, 4 services" (total), "2 services" (category), null (service). */
  subLabel: string | null;
  allocated: number;
  claimed: number;
  booked: number;
  remaining: number;
  /** Fraction (1 = 100%), null when nothing is allocated. */
  utilisation: number | null;
  segments: BarSegment[];
  barLabel: string;
}

export interface TrackerBreakdownView {
  /** Exactly one root: the total, which is always expanded. */
  rows: TreeTableRow<BreakdownRow>[];
  scaleMax: number;
  legend: SegmentedBarLegendItem[];
}

interface CategoryGroup {
  id: string;
  name: string;
  figures: Omit<BreakdownRow, 'kind' | 'code' | 'subLabel' | 'segments' | 'barLabel'>;
  services: TrackerService[];
}

const toRowFigures = (figures: Pick<TrackerFigures, 'allocated' | 'claimed' | 'booked' | 'remaining'>) => ({
  allocated: figures.allocated,
  claimed: figures.claimed,
  booked: figures.booked,
  remaining: figures.remaining,
});

const buildRow = (
  base: Pick<BreakdownRow, 'kind' | 'name' | 'code' | 'subLabel' | 'utilisation'> & UtilisationFigures,
): BreakdownRow => ({
  ...base,
  segments: toUtilisationSegments(base),
  barLabel: `${base.name}: ${describeUtilisation(base)}`,
});

/**
 * Groups the API's one-row-per-(category, service) list into one group per category, in the API's category order.
 * A service with lines in two categories appears once in each group. Services with nothing allocated, booked or
 * claimed are left out; services with a missing or unknown category all go into one 'Uncategorised' group, whose
 * figures are added up here since the API has no category row for it.
 */
const groupServicesByCategory = (
  services: TrackerService[],
  categories: TrackerCategory[],
): { groups: CategoryGroup[]; visibleServices: TrackerService[] } => {
  const visibleServices = services.filter(
    (service) => service.allocated > 0 || service.booked > 0 || service.claimed > 0,
  );
  const groups = new Map<string, CategoryGroup>();
  const knownCategoryIds = new Set(categories.map(({ id }) => id));

  categories.forEach((category) => {
    groups.set(category.id, {
      id: category.id,
      name: category.name,
      figures: { ...toRowFigures(category), name: category.name, utilisation: category.utilization_percentage },
      services: [],
    });
  });

  visibleServices.forEach((service) => {
    const groupId = knownCategoryIds.has(service.category_id) ? service.category_id : UNCATEGORISED_ID;
    let group = groups.get(groupId);
    if (!group) {
      group = {
        id: UNCATEGORISED_ID,
        name: UNCATEGORISED_NAME,
        figures: { allocated: 0, claimed: 0, booked: 0, remaining: 0, name: UNCATEGORISED_NAME, utilisation: null },
        services: [],
      };
      groups.set(UNCATEGORISED_ID, group);
    }
    group.services.push(service);
  });

  const uncategorised = groups.get(UNCATEGORISED_ID);
  if (uncategorised) {
    const sum = (pick: (service: TrackerService) => number) =>
      uncategorised.services.reduce((total, service) => total + pick(service), 0);
    const allocated = sum((service) => service.allocated);
    const booked = sum((service) => service.booked);
    const claimed = sum((service) => service.claimed);
    uncategorised.figures = {
      name: uncategorised.name,
      allocated,
      booked,
      claimed,
      remaining: allocated - booked - claimed,
      utilisation: allocated > 0 ? (booked + claimed) / allocated : null,
    };
  }

  const hasFigures = ({ figures }: CategoryGroup) =>
    figures.allocated !== 0 || figures.booked !== 0 || figures.claimed !== 0;

  return {
    groups: Array.from(groups.values()).filter((group) => group.services.length > 0 || hasFigures(group)),
    visibleServices,
  };
};

export const transformTrackerBreakdown = (tracker: Tracker): TrackerBreakdownView => {
  const { summary, categories = [], services = [] } = tracker;
  const copy = TRACKER_TEXT.breakdown;
  const { groups, visibleServices } = groupServicesByCategory(services, categories);

  const categoryRows: TreeTableRow<BreakdownRow>[] = groups.map((group) => ({
    id: `category-${group.id}`,
    data: buildRow({
      kind: 'category',
      code: null,
      subLabel: pluralise(group.services.length, 'service'),
      ...group.figures,
    }),
    children: group.services.map((service) => ({
      id: `service-${service.category_id}-${service.id}`,
      data: buildRow({
        kind: 'service',
        name: service.name,
        code: service.code,
        subLabel: null,
        ...toRowFigures(service),
        utilisation: service.utilization_percentage,
      }),
    })),
  }));

  const totalRow: TreeTableRow<BreakdownRow> = {
    id: 'total',
    alwaysExpanded: true,
    data: buildRow({
      kind: 'total',
      name: copy.totalName,
      code: null,
      subLabel: `${pluralise(categoryRows.length, 'category', 'categories')}, ${pluralise(
        countDistinctServices(visibleServices),
        'service',
      )}`,
      ...toRowFigures(summary),
      utilisation: summary.utilization_percentage,
    }),
    children: categoryRows,
  };

  const allData: BreakdownRow[] = [totalRow, ...categoryRows, ...categoryRows.flatMap((row) => row.children ?? [])].map(
    (row) => row.data,
  );
  const scaleMax = Math.max(0, ...allData.map((row) => Math.max(row.allocated, row.booked + row.claimed)));
  // The over-allocation part is keyed only when some bar has an orange part
  const hasBookedOverAllocated = allData.some((row) =>
    row.segments.some((segment) => segment.key === 'booked-over-allocated'),
  );
  const legend: SegmentedBarLegendItem[] = [
    { key: 'booked', label: copy.legend.booked, color: TRACKER_FIGURE_COLOURS.booked },
    { key: 'claimed', label: copy.legend.claimed, color: TRACKER_FIGURE_COLOURS.claimed },
    { key: 'remaining', label: copy.legend.remaining, color: TRACKER_FIGURE_COLOURS.remaining },
    ...(hasBookedOverAllocated
      ? [
          {
            key: 'booked-over-allocated',
            label: copy.legend.bookedOverAllocated,
            color: TRACKER_FIGURE_COLOURS.bookedOverAllocated,
          },
        ]
      : []),
  ];

  return { rows: [totalRow], scaleMax, legend };
};

export interface TrackerView {
  summary: TrackerSummaryView;
  overview: TrackerOverviewView;
  breakdown: TrackerBreakdownView;
}

export const buildTrackerView = (tracker: Tracker): TrackerView => ({
  summary: transformTrackerSummary(tracker),
  overview: transformTrackerOverview(tracker),
  breakdown: transformTrackerBreakdown(tracker),
});
